import * as React from 'react';

import { cn } from '../../lib/cn';

// The bordered dashboard grid. Columns are `{ key, header, align, headerAlign, width, cell }`;
// `onRowClick` makes rows openable, and a cell's own control still wins the click.

// A row is openable, so a control inside one has to swallow its own click or the row
// opens behind it. The row is itself [role=button], so it has to lose this match.
const fromOwnControl = (event) => {
  const control = event.target.closest?.(
    'a, button, input, select, textarea, label, [role="button"]'
  );
  return Boolean(control) && control !== event.currentTarget;
};

// `dense` tightens the cell padding to 16px for grids with nine or more columns.
export const DataTable = ({
  className,
  columns,
  rows,
  rowKey,
  onRowClick,
  dense = false,
  empty = 'Nothing to show.',
}) => (
  <div className={cn('w-full overflow-x-auto rounded-[16px] border border-dusq-border', className)}>
    <table className="w-full border-collapse">
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.key}
              scope="col"
              style={column.width ? { width: column.width } : undefined}
              className={cn(
                'border-b border-r border-dusq-border h-[54px] bg-dusq-grey950 py-2 font-dusq text-[12px] font-normal tracking-[0.24px] text-dusq-grey400 last:border-r-0',
                dense ? 'px-4' : 'px-6',
                (column.headerAlign || column.align) === 'left' ? 'text-left' : 'text-center'
              )}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      {/* The wrapper draws the outer edge, so the final row must not double it. */}
      <tbody className="[&>tr:last-child>td]:border-b-0">
        {rows.length === 0 && (
          <tr>
            <td
              colSpan={columns.length}
              className="px-6 py-8 text-center font-dusq text-[12px] text-dusq-grey400"
            >
              {empty}
            </td>
          </tr>
        )}
        {rows.map((row) => (
          <tr
            key={rowKey(row)}
            {...(onRowClick && {
              tabIndex: 0,
              role: 'button',
              onClick: (event) => {
                if (fromOwnControl(event)) return;
                onRowClick(row);
              },
              onKeyDown: (event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onRowClick(row);
                }
              },
              className:
                'cursor-pointer hover:bg-dusq-grey950 focus-visible:outline focus-visible:outline-1 focus-visible:outline-dusq-accent',
            })}
          >
            {columns.map((column) => (
              <td
                key={column.key}
                className={cn(
                  'border-b border-r border-dusq-border h-[60px] py-2 align-middle font-dusq text-[14px] tabular-nums tracking-[0.28px] text-dusq-emphasis-disabled last:border-r-0',
                  dense ? 'px-4' : 'px-6',
                  column.align === 'left' ? 'text-left' : 'text-center',
                  column.cellClassName
                )}
              >
                {column.cell(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
